/**
 * Blur gating, and the switch that decides whether it runs.
 *
 * Wired into the video capture modules, and off unless the client's config has
 * "blurred": true -- see setBlurFromConfig, which each of them calls with the
 * config the same way the flash is switched on from config["torch"].
 *
 * Gating:
 *
 *   on    in the video modules, every frame that is going to be uploaded is
 *         judged first by blurMetrics' rules (Flag 1) -- a full-frame raw
 *         Laplacian under 630, else 12+ of 16 blurry cells or a norm score
 *         under 125,000, on the vehicle crop with parts detected and on the
 *         full frame without. A frame judged blurry is NOT uploaded, and while
 *         a region's frames are blurry its dial segment is held grey instead of
 *         going blue/green. That hold is one-way: once a segment has been drawn
 *         blue or green it is never taken back. StencilVideo checks nothing per
 *         frame; it judges only the frames it keeps -- the stencil shots at the
 *         two moments a stencil changes colour, and its interim frames.
 *   off   nothing here runs and uploads are not gated -- the app behaves exactly
 *         as it did before this feature existed.
 *
 * The torch is a separate, independent switch: it reads brightness, this reads
 * focus.
 *
 * Blurry frames are skipped and the next frame is tried -- a region can always
 * complete once the user holds steady, so there is no way to get stuck.
 *
 * Everything is recorded per region so the on-screen panel can show what was
 * judged and why. Never throws: a failed analysis is reported as "unknown" and
 * does not block an upload.
 */

import { evaluateBlurFrame } from "./blurMetrics";

export const MODES = { TORCH: "torch", BLUR: "blur", NORMAL: "normal" };

// The order setMode validates against, and the order cycleMode/stepMode follow.
export const MODE_ORDER = [MODES.TORCH, MODES.BLUR, MODES.NORMAL];

const FRAME_LOG_SIZE = 8; // recent frames kept for the panel

// Off until a client's config asks for it. NORMAL rather than TORCH because the
// flash is no longer one of this switch's positions -- torchController owns that
// independently -- so the only question left here is gating on or off.
let mode = MODES.NORMAL;

let frames = []; // recent verdicts, newest last
let events = []; // mode switches and blocked uploads
let regionStats = {}; // region -> { seen, blurry, sharp }
let lastVerdict = null; // verdict for the most recent analysed frame
// Regions whose dial segment has already been drawn blue or green. Blur can hold
// a grey segment grey, but it can never take a colour back once the user has seen
// it, so these are exempt from masking for the rest of the inspection.
let revealed = {};
let blockedUploads = 0;
let analysing = false; // guards against overlapping analyses

const hhmmss = () => new Date().toTimeString().slice(0, 8);

const pushEvent = (msg) => {
  events.push(`${hhmmss()} ${msg}`);
  if (events.length > 20) events.shift();
};

export const getMode = () => mode;
export const isTorchMode = () => mode === MODES.TORCH;
export const isBlurMode = () => mode === MODES.BLUR;
export const isNormalMode = () => mode === MODES.NORMAL;

/** Switch modes. Anything unrecognised falls back to torch. Returns the mode. */
export const setMode = (next) => {
  const wanted = MODE_ORDER.includes(next) ? next : MODES.TORCH;
  if (wanted === mode) return mode;
  const from = mode;
  mode = wanted;
  // A verdict from the previous mode must not gate the first frames of this one.
  lastVerdict = null;
  frames = [];
  pushEvent(`MODE ${from.toUpperCase()} -> ${mode.toUpperCase()}`);
  return mode;
};

/**
 * Turn blur gating on or off. Off leaves everything else in here a no-op -- a
 * client without gating pays nothing beyond a returned null.
 */
export const setBlurEnabled = (enabled) =>
  setMode(enabled ? MODES.BLUR : MODES.NORMAL);

/**
 * Turn blur gating on for this client when its config has "blurred": true. The
 * key has to be present AND true; absent, or present and false, leaves it off.
 */
export const setBlurFromConfig = (config) =>
  setBlurEnabled(
    !!config && Object.keys(config).includes("blurred") && !!config["blurred"]
  );

/** Next mode in MODE_ORDER, wrapping. */
export const cycleMode = () =>
  setMode(MODE_ORDER[(MODE_ORDER.indexOf(mode) + 1) % MODE_ORDER.length]);

/**
 * Step one position through the swipe order. dir > 0 moves right, dir < 0 left.
 * Clamped rather than wrapped, so a drag cannot skip past the end and reappear at
 * the other side.
 */
export const stepMode = (dir) => {
  const i = MODE_ORDER.indexOf(mode);
  const next = Math.min(MODE_ORDER.length - 1, Math.max(0, i + (dir > 0 ? 1 : -1)));
  return setMode(MODE_ORDER[next]);
};

/** Clear everything for a new inspection. Leaves the chosen mode alone. */
export const resetBlur = () => {
  frames = [];
  events = [];
  regionStats = {};
  lastVerdict = null;
  revealed = {};
  blockedUploads = 0;
  analysing = false;
};

const statsFor = (region) => {
  const key = region || "unknown";
  if (!regionStats[key]) regionStats[key] = { seen: 0, blurry: 0, sharp: 0 };
  return regionStats[key];
};

/**
 * Judge one frame by blurMetrics' rules and record the verdict against its
 * region. `detections` are the frame's part detections: their boxes make the
 * vehicle crop the grid and norm checks run on, and with none they run on the
 * full frame.
 *
 * Not throttled like the torch check, because the answer gates whether THIS
 * frame may be uploaded, so a stale verdict would gate the wrong frame.
 * Overlapping calls are skipped rather than queued.
 *
 * Resolves the verdict, or null while gating is off. `unknown` means the
 * analysis could not run, and the caller should let the frame through.
 */
export const checkFrameForBlur = async (base64, detections, region) => {
  if (mode !== MODES.BLUR) return null;
  if (!base64) return null;
  if (analysing) return lastVerdict; // reuse the newest verdict rather than queue
  analysing = true;

  try {
    const v = await evaluateBlurFrame(base64, detections || []);
    const stats = statsFor(region);
    stats.seen += 1;
    if (v.blurred) stats.blurry += 1;
    else stats.sharp += 1;

    const normScore = v.normScore === null ? null : Math.round(v.normScore);
    lastVerdict = {
      blurred: v.blurred,
      unknown: false,
      blurScore: Math.round(v.wholeLapVar),
      rawLapVar: Math.round(v.rawLapVar),
      normScore,
      vehiclePartsDetected: v.vehiclePartsDetected,
      filteredByRaw: v.filteredByRaw,
      filteredByCells: v.filteredByCells,
      filteredByNorm: v.filteredByNorm,
      // What the verdict was actually taken on, so a frame that was let through
      // and a frame that was blocked can be told apart by how close it came to
      // the bar rather than by a single opaque boolean.
      blurryCells: v.blurryCells,
      blurryCellsNeeded: v.cellsNeeded,
      gridCells: 16,
      region: region || "unknown",
      t: Date.now(),
    };

    frames.push({
      blurred: v.blurred,
      bs: lastVerdict.blurScore,
      raw: lastVerdict.rawLapVar,
      norm: normScore,
      cells: v.blurryCells,
      total: 16,
      region: lastVerdict.region,
      t: hhmmss(),
    });
    if (frames.length > FRAME_LOG_SIZE) frames.shift();

    return lastVerdict;
  } catch (e) {
    // Never let a failed analysis block the inspection.
    lastVerdict = { blurred: false, unknown: true, region: region || "unknown" };
    return lastVerdict;
  } finally {
    analysing = false;
  }
};

/**
 * May the frame just analysed be uploaded?
 *
 * True while gating is off, true when the analysis could not run, and false only
 * when the last frame was positively judged blurry.
 */
export const mayUploadFrame = () => {
  if (mode !== MODES.BLUR) return true;
  if (!lastVerdict || lastVerdict.unknown) return true;
  return !lastVerdict.blurred;
};

/** Record that an upload was skipped, for the panel and the sheet. */
export const noteBlockedUpload = (region) => {
  blockedUploads += 1;
  pushEvent(`upload skipped (blurry) ${region || ""}`.trim());
};

export const getBlockedUploads = () => blockedUploads;
export const getLastVerdict = () => lastVerdict;

/**
 * Has every frame seen for this region been blurry?
 *
 * Only meaningful once at least one frame has been seen; a region nobody has
 * pointed the camera at yet is not "all blurry".
 */
export const isRegionAllBlurred = (region) => {
  const s = regionStats[region || "unknown"];
  return !!(s && s.seen > 0 && s.blurry === s.seen);
};

export const getRegionStats = (region) => {
  const s = regionStats[region || "unknown"];
  return s ? { ...s } : { seen: 0, blurry: 0, sharp: 0 };
};

export const getAllRegionStats = () => {
  const out = {};
  for (const k of Object.keys(regionStats)) out[k] = { ...regionStats[k] };
  return out;
};

/**
 * Should this region's segment be held grey on the strength of the last verdict?
 *
 * Only ever holds a segment that is still grey. A segment already drawn blue or
 * green is never taken back -- see `revealed`.
 */
const shouldHoldGrey = (regionsCovered, region) => {
  if (!region || !(region in regionsCovered)) return false;
  if (revealed[region]) return false;
  if (!lastVerdict || lastVerdict.unknown || !lastVerdict.blurred) return false;
  // The verdict must belong to THIS region. Without this check, moving to a new
  // region straight after a blurry frame would grey the new one on the strength
  // of the previous region's frame.
  return lastVerdict.region === region;
};

/**
 * A copy of the coverage map with the current region forced back to NOT_COVERED
 * while its frames are blurry, so the dial segment stays grey instead of turning
 * blue or green.
 *
 * One-way, and latched: this can only keep a segment from colouring in the first
 * place. Once a segment has actually been drawn blue or green it is recorded in
 * `revealed` and never masked again, so a blurry frame over a region the user has
 * already finished cannot take its colour away. Progress on the dial only ever
 * moves forwards, which is what the user is reading it for.
 *
 * A copy on purpose: the guidance module's own map is left untouched, so this only
 * changes what is drawn, never what the guidance believes. With gating off the map
 * is passed straight through.
 */
export const maskRegionsForBlur = (regionsCovered, region) => {
  if (mode !== MODES.BLUR) return regionsCovered;
  if (!regionsCovered || typeof regionsCovered !== "object") return regionsCovered;

  const masked = shouldHoldGrey(regionsCovered, region)
    ? { ...regionsCovered, [region]: 0 }
    : regionsCovered;

  // Latch on what is actually DRAWN, not on what guidance believes. On the frame
  // where a blurry region first turns covered, the mask above has already put it
  // back to grey, so it is not recorded here and the hold survives into the next
  // frame. It is recorded the first time it reaches the dial with a colour.
  for (const key of Object.keys(masked))
    if (masked[key] !== 0) revealed[key] = true;

  return masked;
};

/** Per-frame fields for logging, while gating is on. */
export const getSheetFields = (region) => {
  const stats = getRegionStats(region);
  return {
    mode,
    blurred: lastVerdict ? !!lastVerdict.blurred : null,
    blurryCells: lastVerdict ? lastVerdict.blurryCells : null,
    gridCells: lastVerdict ? lastVerdict.gridCells : null,
    rawLapVar: lastVerdict ? lastVerdict.rawLapVar : null,
    normScore: lastVerdict ? lastVerdict.normScore : null,
    uploadBlocked: mode === MODES.BLUR && !mayUploadFrame(),
    region: region || "",
    regionSeen: stats.seen,
    regionBlurry: stats.blurry,
    regionAllBlur: isRegionAllBlurred(region),
  };
};

/** HTML for the on-screen panel: mode, the last frames, and per-region tallies. */
export const getBlurDebugHtml = () => {
  try {
    if (mode === MODES.NORMAL)
      return "<b>NORMAL MODE</b> &nbsp; <i>no torch, no blur gating</i>";
    if (mode !== MODES.BLUR)
      return "<b>torch mode</b> &nbsp; <i>blur checks idle</i>";
    const modeLabel = '<span style="color:#ffab40"><b>BLUR MODE</b></span>';

    const rows = frames
      .map(
        (f, i) =>
          `${i + 1}. ${f.blurred ? "BLUR" : "ok  "} ` +
          // A frame the raw check filtered has no grid or norm figures.
          `${f.cells != null ? `${f.cells}/${f.total} cells ` : ""}` +
          `raw=${f.raw} bs=${f.bs}${f.norm != null ? ` norm=${f.norm}` : ""} ${f.region}`
      )
      .join("<br/>");

    const regions = Object.keys(regionStats)
      .map((r) => {
        const s = regionStats[r];
        const all = s.seen > 0 && s.blurry === s.seen ? " ALL-BLUR" : "";
        return `${r}: ${s.blurry}/${s.seen} blurry${all}`;
      })
      .join("<br/>");

    return (
      `${modeLabel} &nbsp; <b>skipped ${blockedUploads}</b><br/>` +
      `<b>last ${frames.length} frames:</b><br/>${rows || "-"}<br/>` +
      `<b>per region:</b><br/>${regions || "-"}<br/>` +
      `<b>events:</b><br/>${events.slice(-5).join("<br/>") || "-"}`
    );
  } catch (e) {
    return "";
  }
};
